import React, { useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import AWS from 'aws-sdk';
import Layout from '../components/Layout';
import '../styles/recommendation-systems.css';
import '../styles/service-fix.css'; // Fix for dark text on dark background

const RecommendationSystems = ({ user }) => {
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    useCase: '',
    industry: 'retail'
  });
  const [submitStatus, setSubmitStatus] = useState('');
  
  const isAuthenticated = user !== null;

  if (!isAuthenticated) {
    return (
      <Layout>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <h2>Authentication Required</h2>
          <p>Please sign in to access recommendation system services.</p>
        </div>
      </Layout>
    );
  }

  const handleContactInfoChange = (field, value) => {
    setContactInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactInfo.name || !contactInfo.email || !contactInfo.useCase) {
      setSubmitStatus('Please provide your name, email address, and use case.');
      return;
    }
    
    setSubmitStatus('Submitting...');
    
    try {
      // Get AWS credentials from Amplify Auth
      const session = await fetchAuthSession();
      const credentials = session.credentials;
      
      // Configure AWS SDK with proper credentials
      AWS.config.update({
        region: 'us-east-1',
        credentials: {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken
        }
      });
      
      // Send email notification
      const ses = new AWS.SES();
      const emailParams = {
        Source: 'info@relufox.ai',
        Destination: {
          ToAddresses: ['info@relufox.ai']
        },
        Message: {
          Subject: {
            Data: 'New Recommendation Systems Inquiry - Fox River AI'
          },
          Body: {
            Text: {
              Data: `A new inquiry has been submitted for recommendation systems services:\n\nCONTACT INFORMATION:\nName: ${contactInfo.name}\nEmail: ${contactInfo.email}\nCompany: ${contactInfo.company || 'Not provided'}\nPhone: ${contactInfo.phone || 'Not provided'}\n\nINDUSTRY: ${contactInfo.industry}\n\nUSE CASE:\n${contactInfo.useCase}\n\nPlease follow up with this customer.`
            }
          }
        }
      };
      
      await ses.sendEmail(emailParams).promise();
      
      setSubmitStatus('Thank you! Your inquiry has been submitted. We will contact you within 24 hours.');
      setContactInfo({
        name: '',
        email: '',
        company: '',
        phone: '',
        useCase: '',
        industry: 'retail'
      });
      
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus(`Submission failed: ${error.message || 'Unknown error'}. Please try again or contact us directly.`);
    }
  };

  return (
    <Layout>
      <div className="recommendation-systems-page">
        <div className="hero-section">
          <h1>Recommendation Systems</h1>
          <p className="hero-subtitle">Personalized experiences powered by AI that learn from real customer behavior</p>
        </div>

        <div className="content-container">
          <section className="service-overview">
            <h2>Smarter Recommendations, Better Results</h2>
            <p className="section-intro">Deliver smarter, more personalized experiences with our AI-powered recommendation systems. Whether you're recommending products, services, or content, we build models that learn from real customer behavior — not just rules or filters.</p>
            
            <div className="services-grid">
              <div className="service-item">
                <h3>Product Recommendations</h3>
                <p>Increase basket size and conversion rates with personalized product suggestions</p>
              </div>
              <div className="service-item">
                <h3>Content Personalization</h3>
                <p>Keep users engaged with tailored content recommendations</p>
              </div>
              <div className="service-item">
                <h3>Service Suggestions</h3>
                <p>Recommend relevant services based on customer needs and history</p>
              </div>
              <div className="service-item">
                <h3>Cross-Selling & Upselling</h3>
                <p>Identify opportunities to increase customer lifetime value</p>
              </div>
            </div>
          </section>

          <section className="technology-section">
            <h2>Our Approach</h2>
            <div className="tech-features">
              <div className="tech-item">
                <h4>🔄 Collaborative Filtering</h4>
                <p>Recommendations based on similar users' preferences and behaviors</p>
              </div>
              <div className="tech-item">
                <h4>📊 Content-Based Models</h4>
                <p>Suggestions based on item attributes and user preferences</p>
              </div>
              <div className="tech-item">
                <h4>🧠 Deep Learning</h4>
                <p>Advanced neural networks that identify complex patterns</p>
              </div>
              <div className="tech-item">
                <h4>📈 Vector Embeddings</h4>
                <p>Sophisticated representations of items and user preferences</p>
              </div>
            </div>
          </section>

          <section className="benefits-section">
            <h2>Business Benefits</h2>
            <div className="benefits-grid">
              <div className="benefit-item">
                <h3>Increased Conversions</h3>
                <p>Higher conversion rates through personalized recommendations</p>
              </div>
              <div className="benefit-item">
                <h3>Improved Retention</h3>
                <p>Keep customers coming back with relevant suggestions</p>
              </div>
              <div className="benefit-item">
                <h3>Higher Satisfaction</h3>
                <p>Enhance customer experience with personalized interactions</p>
              </div>
              <div className="benefit-item">
                <h3>Larger Order Values</h3>
                <p>Increase average order value with smart cross-selling</p>
              </div>
            </div>
          </section>

          <section className="inquiry-section">
            <h2>Enhance Your Customer Experience</h2>
            <p>Tell us about your business, and we'll explore how recommendation systems can help you deliver more personalized experiences.</p>
            
            <form className="inquiry-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <input 
                  type="text" 
                  placeholder="Full Name *" 
                  value={contactInfo.name}
                  onChange={(e) => handleContactInfoChange('name', e.target.value)}
                  className="form-input"
                  required
                />
                <input 
                  type="email" 
                  placeholder="Email Address *" 
                  value={contactInfo.email}
                  onChange={(e) => handleContactInfoChange('email', e.target.value)}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-row">
                <input 
                  type="text" 
                  placeholder="Company (Optional)" 
                  value={contactInfo.company}
                  onChange={(e) => handleContactInfoChange('company', e.target.value)}
                  className="form-input"
                />
                <input 
                  type="tel" 
                  placeholder="Phone (Optional)" 
                  value={contactInfo.phone}
                  onChange={(e) => handleContactInfoChange('phone', e.target.value)}
                  className="form-input"
                />
              </div>
              <div className="form-row">
                <select
                  value={contactInfo.industry}
                  onChange={(e) => handleContactInfoChange('industry', e.target.value)}
                  className="form-select"
                >
                  <option value="retail">Retail / E-commerce</option>
                  <option value="media">Media / Content</option>
                  <option value="services">Professional Services</option>
                  <option value="finance">Financial Services</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <textarea
                placeholder="Describe your business and recommendation needs *"
                value={contactInfo.useCase}
                onChange={(e) => handleContactInfoChange('useCase', e.target.value)}
                className="form-textarea"
                rows="5"
                required
              ></textarea>
              <button type="submit" className="submit-btn">
                Request Consultation
              </button>
              {submitStatus && <p className="submit-status">{submitStatus}</p>}
            </form>
            
            <div className="contact-alternative">
              <p>Prefer to discuss your needs directly?</p>
              <a href="tel:6309408837" className="contact-btn">Call (630) 940-8837</a>
              <p>or email us at <a href="mailto:info@relufox.ai">info@relufox.ai</a></p>
            </div>
          </section>

          <section className="process-section">
            <h2>Our Implementation Process</h2>
            <div className="process-steps">
              <div className="step">
                <div className="step-number">1</div>
                <h3>Data Assessment</h3>
                <p>Evaluating your data sources and recommendation opportunities</p>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <h3>Model Development</h3>
                <p>Building and training recommendation algorithms for your specific needs</p>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <h3>Integration</h3>
                <p>Implementing the recommendation system into your existing platforms</p>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <h3>Optimization</h3>
                <p>Continuous improvement based on performance and user feedback</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default RecommendationSystems;